import {
  createPersonSchema,
  MEMBERSHIP_STATUSES,
  type CreatePersonFormInput,
  type CreatePersonInput,
} from '@church/domain'
import {
  notify,
  submitSafely,
  useCampuses,
  useCreatePerson,
  usePeopleList,
  usePeopleSearch,
  useAccess,
  useTags,
} from '@church/data'
import { faCopy, faFileImport, faFilter, faUserPlus, faXmark } from '@church/ui-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useEffect, useId, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  IconButton,
  Input,
  PageHeader,
  Select,
  ErrorState,
  Icon,
  InlineSpinner,
  ListSkeleton,
  SidePanel,
  Spinner,
} from '../components/ui.tsx'
import { PageAction } from '../components/PageAction.tsx'
import { pressableRow } from '../components/row-press.ts'
import { DisplayOptions } from '../components/DisplayOptions.tsx'
import { MoreActions } from '../components/MoreActions.tsx'
import { useListDisplay } from '../lib/display.ts'
import { remToPx } from '../lib/rem.ts'

export const Route = createFileRoute('/_authed/_shell/people/')({
  component: PeopleIndexScreen,
})

type Sort = 'lastName' | '-lastName' | 'createdAt' | '-createdAt'

interface Row {
  id: string
  firstName: string
  lastName: string
  nickname: string | null
  membershipStatus: string
  campusId: string | null
  primaryEmail: string | null
  primaryPhone: string | null
  contactsHidden: boolean
}

/** 3.5rem: 56px on a phone, 49px on a computer (a 14px root). */
const ROW_HEIGHT = remToPx(3.5)

const SORT_LABELS: Record<Sort, string> = {
  lastName: 'Last name A–Z',
  '-lastName': 'Last name Z–A',
  '-createdAt': 'Newest first',
  createdAt: 'Oldest first',
}

/** A filter in force, said in words, with an × to drop it. */
function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-brand-soft py-0.5 pr-0.5 pl-3 text-sm font-medium text-content-brand capitalize">
      {label}
      <button
        type="button"
        aria-label={`Remove ${label}`}
        onClick={onRemove}
        className="inline-flex size-7 items-center justify-center rounded-full hover:bg-surface-sunken"
      >
        <Icon icon={faXmark} className="size-3" />
      </button>
    </span>
  )
}

/** What a person's row shows on a phone; every column shows from `sm` up. */
const ROW_FIELDS = [
  { id: 'status', label: 'Status', default: true },
  { id: 'contact', label: 'Email or phone', default: true },
  { id: 'campus', label: 'Campus', default: false },
] as const

function PeopleIndexScreen() {
  const navigate = useNavigate()
  const access = useAccess()
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState('')
  const [tagId, setTagId] = useState('')
  const [sort, setSort] = useState<Sort>('lastName')
  const [adding, setAdding] = useState(false)
  const create = useCreatePerson()
  const addFormId = useId()
  const startAdding = () => {
    create.reset()
    setAdding(true)
  }
  // Phones keep the filters behind a button; from sm up they are always shown.
  const [filtering, setFiltering] = useState(false)
  const display = useListDisplay('people', ROW_FIELDS)
  const filtered = Boolean(status || tagId) || sort !== 'lastName'

  const searching = query.trim().length >= 2
  const list = usePeopleList({ status: status || undefined, tagId: tagId || undefined, sort })
  const search = usePeopleSearch(query)
  const { data: campuses } = useCampuses()
  const { data: tags } = useTags()

  const rows: Row[] = searching ? search.results : list.people
  const campusName = (id: string | null) =>
    campuses?.find((c) => c.id === id)?.name ?? (id ? '—' : '')

  // The directory is virtualized — always (CLAUDE.md): 50k-person orgs are the
  // sizing target, and the scroll container fetches the next page near the end.
  const scrollRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
    // A little room under the last row, so it never sits flush on the card's edge.
    paddingEnd: 8,
  })

  const virtualItems = virtualizer.getVirtualItems()
  const lastIndex = virtualItems[virtualItems.length - 1]?.index ?? 0
  useEffect(() => {
    if (
      !searching &&
      list.hasNextPage &&
      !list.isFetchingNextPage &&
      lastIndex >= rows.length - 15
    ) {
      void list.fetchNextPage()
    }
  }, [lastIndex, rows.length, searching, list])

  // The rest of the page's actions, behind ⋯ on a phone.
  const actions = (
    <>
      {access.has('people', 'administrator') && (
        <MoreActions
          actions={[
            {
              label: 'Duplicates',
              icon: faCopy,
              onClick: () => void navigate({ to: '/people/duplicates' }),
            },
          ]}
        />
      )}
    </>
  )

  return (
    <div className="flex h-full flex-col space-y-4">
      <PageHeader title="People" />

      <SidePanel
        open={adding}
        onOpenChange={(next) => {
          if (!next) setAdding(false)
        }}
        title="Add a person"
        primary={
          <Button
            type="submit"
            form={addFormId}
            icon={faUserPlus}
            className="py-1.5"
            pending={create.isPending}
            pendingLabel="Adding…"
          >
            Add
          </Button>
        }
      >
        <AddPersonForm
          key={String(adding)}
          create={create}
          formId={addFormId}
          onDone={() => setAdding(false)}
        />
      </SidePanel>

      <div className="flex items-center gap-1 sm:hidden">
        <Input
          placeholder="Search name, email or phone…"
          aria-label="Search people"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <span className="relative">
          <IconButton
            icon={faFilter}
            label={filtering ? 'Hide filters' : 'Filters'}
            aria-expanded={filtering}
            className={filtering || filtered ? 'text-content-brand' : ''}
            onClick={() => setFiltering((v) => !v)}
          />
        </span>
        <DisplayOptions fields={ROW_FIELDS} shown={display.shown} onChange={display.set} />
        {actions}
      </div>

      <div className={`flex-wrap gap-2 sm:flex ${filtering ? 'flex' : 'hidden'}`}>
        <Input
          className="hidden sm:block sm:max-w-xs"
          placeholder="Search by name, email or phone…"
          aria-label="Search people"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Select
          aria-label="Membership status"
          className="min-w-32 flex-1 sm:max-w-40"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          {MEMBERSHIP_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        {tags && tags.length > 0 && (
          <Select
            aria-label="Tag filter"
            className="min-w-32 flex-1 sm:max-w-44"
            value={tagId}
            onChange={(e) => setTagId(e.target.value)}
          >
            <option value="">All tags</option>
            {tags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name} ({tag.personCount})
              </option>
            ))}
          </Select>
        )}
        <Select
          aria-label="Sort by"
          className="min-w-32 flex-1 sm:max-w-44"
          value={sort}
          onChange={(e) => setSort(e.target.value as Sort)}
        >
          {(Object.keys(SORT_LABELS) as Sort[]).map((key) => (
            <option key={key} value={key}>
              {SORT_LABELS[key]}
            </option>
          ))}
        </Select>
        {(search.isSearching || list.isLoading) && (
          <span role="status" className="flex items-center">
            <InlineSpinner className="size-4 text-content-brand" />
            <span className="sr-only">Loading…</span>
          </span>
        )}
        {/* Once: a button here from sm up; on a phone it floats (it is portalled
            out, so this row being hidden there doesn't hide it). */}
        <div className="ml-auto hidden items-center gap-2 sm:flex">
          {actions}
          {access.has('people', 'editor') && (
            <PageAction icon={faUserPlus} label="New person" onClick={startAdding} />
          )}
        </div>
      </div>

      {/* What the list is showing, and the way back to everyone. */}
      {(filtered || rows.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-content-muted">
          <span className="tabular-nums">
            {searching
              ? `${rows.length} ${rows.length === 1 ? 'match' : 'matches'}`
              : `${rows.length}${list.complete ? '' : '+'} ${rows.length === 1 ? 'person' : 'people'}`}
          </span>
          {status && <FilterChip label={`Status: ${status}`} onRemove={() => setStatus('')} />}
          {tagId && (
            <FilterChip
              label={`Tag: ${tags?.find((t) => t.id === tagId)?.name ?? 'tag'}`}
              onRemove={() => setTagId('')}
            />
          )}
          {sort !== 'lastName' && (
            <FilterChip label={SORT_LABELS[sort]} onRemove={() => setSort('lastName')} />
          )}
          {filtered && (
            <button
              type="button"
              className="rounded-control px-2 py-1 font-medium text-content-brand hover:bg-surface-sunken"
              onClick={() => {
                setStatus('')
                setTagId('')
                setSort('lastName')
              }}
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* A column: the scroll box takes what the header row leaves, so its last row is reachable. */}
      <Card bleed className="flex min-h-0 flex-1 flex-col">
        {/* A phone shows what its display options choose; sm and up shows every column. */}
        <div className="hidden gap-4 border-b border-border-default bg-surface-heading px-4 py-2 text-xs font-medium uppercase tracking-wide text-content-muted sm:grid sm:grid-cols-[2fr_1fr_1fr_2fr]">
          <span className="flex-1">Name</span>
          <span>Status</span>
          <span className="hidden sm:block">Campus</span>
          <span className="hidden sm:block">Contact</span>
        </div>

        {/* A failure is said, never shown as an empty directory. */}
        {(searching ? search.error : list.error) && rows.length === 0 ? (
          <ErrorState
            error={searching ? search.error : list.error}
            safe="Nothing was changed: the directory is safe on the server."
            retrying={!searching && list.isRefetching}
            onRetry={() => void (searching ? search.refetch() : list.refetch())}
          />
        ) : list.isLoading && !searching ? (
          <ListSkeleton rows={8} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={
              searching ? 'No matches' : filtered ? 'Nobody matches these filters' : 'No people yet'
            }
            hint={searching ? 'Search is typo-tolerant — try a shorter fragment.' : undefined}
            action={
              searching ? undefined : filtered ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setStatus('')
                    setTagId('')
                    setSort('lastName')
                  }}
                >
                  Clear filters
                </Button>
              ) : access.has('people', 'editor') ? (
                <>
                  <Button icon={faUserPlus} onClick={startAdding}>
                    New person
                  </Button>
                  {access.has('people', 'administrator', { orgWide: true }) && (
                    <Button
                      variant="secondary"
                      icon={faFileImport}
                      onClick={() => void navigate({ to: '/people/import' })}
                    >
                      Import a CSV
                    </Button>
                  )}
                </>
              ) : undefined
            }
          />
        ) : (
          <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto" style={{ contain: 'strict' }}>
            <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
              {virtualItems.map((item) => {
                const person = rows[item.index]
                if (!person) return null
                return (
                  <Link
                    key={person.id}
                    to="/people/$id"
                    params={{ id: person.id }}
                    className={`absolute inset-x-0 flex items-center gap-4 px-4 text-left text-sm no-underline sm:grid sm:grid-cols-[2fr_1fr_1fr_2fr] ${pressableRow}`}
                    style={{ height: ROW_HEIGHT, transform: `translateY(${item.start}px)` }}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-base font-medium text-content-default">
                        {person.lastName}, {person.nickname ?? person.firstName}
                      </span>
                      <span className="block truncate text-xs text-content-muted sm:hidden">
                        {[
                          display.shown.contact
                            ? person.contactsHidden
                              ? 'Hidden (minor)'
                              : (person.primaryEmail ?? person.primaryPhone)
                            : null,
                          display.shown.campus ? campusName(person.campusId) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                    <span className={display.hide('status')}>
                      <span className="sm:hidden">
                        <Chip>
                          <span className="capitalize">{person.membershipStatus}</span>
                        </Chip>
                      </span>
                      <span className="hidden capitalize text-content-muted sm:inline">
                        {person.membershipStatus}
                      </span>
                    </span>
                    <span className="hidden truncate text-content-muted sm:block">
                      {campusName(person.campusId)}
                    </span>
                    <span className="hidden truncate text-content-muted sm:block">
                      {person.contactsHidden
                        ? 'Hidden (minor)'
                        : (person.primaryEmail ?? person.primaryPhone ?? '')}
                    </span>
                  </Link>
                )
              })}
            </div>
            {list.isFetchingNextPage && (
              <div className="p-3">
                <Spinner label="Loading more…" />
              </div>
            )}
          </div>
        )}
      </Card>
      {!access.has('people', 'viewer') && (
        <p
          role="alert"
          className="rounded-control bg-warning-soft px-3 py-2.5 text-sm text-warning-content"
        >
          You don’t have access to People. Ask a church admin to give your role People.
        </p>
      )}
    </div>
  )
}

function AddPersonForm({
  create,
  formId,
  onDone,
}: {
  create: ReturnType<typeof useCreatePerson>
  formId: string
  onDone: () => void
}) {
  const navigate = useNavigate()
  const { data: campuses } = useCampuses()

  const form = useForm<CreatePersonFormInput, unknown, CreatePersonInput>({
    resolver: zodResolver(createPersonSchema),
    defaultValues: { membershipStatus: 'visitor' },
  })

  const submit = form.handleSubmit(async (input) => {
    const created = await create.mutateAsync({
      ...input,
      emails: input.emails?.filter((e) => e.email),
      phones: input.phones?.filter((p) => p.number),
    })
    onDone()
    notify({
      title: `Added ${input.firstName} ${input.lastName}`,
      description: 'Their page is open: add a household, tags or notes next.',
    })
    await navigate({ to: `/people/${created.id}` })
  })

  return (
    <form id={formId} onSubmit={submitSafely(submit)} className="space-y-3" noValidate>
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name" error={form.formState.errors.firstName?.message}>
          <Input {...form.register('firstName')} />
        </Field>
        <Field label="Last name" error={form.formState.errors.lastName?.message}>
          <Input {...form.register('lastName')} />
        </Field>
        <Field label="Status">
          <Select {...form.register('membershipStatus')}>
            {MEMBERSHIP_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Campus">
          <Select
            {...form.register('campusId', {
              setValueAs: (v: string) => (v === '' ? null : v),
            })}
          >
            <option value="">No campus</option>
            {(campuses ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Email" error={form.formState.errors.emails?.[0]?.email?.message}>
          <Input type="email" {...form.register('emails.0.email')} />
        </Field>
        <Field label="Phone" error={form.formState.errors.phones?.[0]?.number?.message}>
          <Input {...form.register('phones.0.number')} />
        </Field>
        <Field label="Birthdate" error={form.formState.errors.birthdate?.message}>
          <Input
            type="date"
            {...form.register('birthdate', { setValueAs: (v: string) => v || null })}
          />
        </Field>
      </div>

      <ErrorNote error={create.error} />
    </form>
  )
}
